import {
  Component,
  OnInit,
  OnChanges,
  Input,
  HostListener,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { BarGraphDataDTO } from "psa-tnt-models";
import {
  AnalyticsForGraphics,
  AnalyticsControllerService
} from "../../../../services/analytics-controller.service";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import {
  defaultBarLayout,
  defaultLineLayout,
  defaultBarConfig,
  defaultLineConfig
} from "../graph-config";

import { COLOR_PALETTE } from "../../../../constants/chart-colors";
import { PlotlySharedModule } from "angular-plotly.js";
import { SvgIconComponent } from "angular-svg-icon";

const GraphType = {
  BAR: "bar",
  LINE: "scatter" // lines
} as const;

type GraphType = (typeof GraphType)[keyof typeof GraphType];

@Component({
  selector: "app-graphs-displayer",
  templateUrl: "./graphs-displayer.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./graphs-displayer.component.scss"],
  imports: [PlotlySharedModule, SvgIconComponent, TranslateModule]
})
export class GraphsDisplayerComponent implements OnInit, OnChanges {
  @Input() datas!: AnalyticsForGraphics[];
  @Input() containerTypes!: string[];
  @Input() filterDays!: number;
  @Input() filterSites!: string[];
  @Input() filterArrivalSites!: string[];
  @Input() filterDepartureSites!: string[];
  @Input() filterAlertType!: string;
  @Input() filterContainerCategory!: string;
  @Input() filterPackaging!: string[];
  @Input() config!: any;

  public graphs: any[] = [];
  public selectedSiteDrilldown:
    | {
        site: string;
        graphIndex: number;
        graphTitle: string;
      }
    | undefined;
  public drilldownGraph: any | undefined;

  constructor(
    public analyticsService: AnalyticsControllerService,
    private translate: TranslateService
  ) {
    translate.onLangChange.subscribe(() => {
      if (this.graphs?.length) {
        this.applyOnAllGraphs(this.updateGraphTitle.bind(this));
      }
    });
  }

  ngOnInit(): void {
    this.prepareGraphs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.datas ||
      changes.filterDays ||
      changes.filterSites ||
      changes.filterArrivalSites ||
      changes.filterDepartureSites ||
      changes.filterAlertType ||
      changes.filterContainerCategory ||
      changes.filterPackaging
    ) {
      this.prepareGraphs();
    }
  }

  @HostListener("window:resize")
  public onResize(): void {
    this.applyOnAllGraphs(this.resizeConfigLayout.bind(this));
    if (!this.drilldownGraph) {
      return;
    }

    this.drilldownGraph.layout = {
      ...this.drilldownGraph.layout,
      width: 0.97 * window.innerWidth,
      height: 0.25 * window.innerHeight,
      margin: {
        l: 80,
        r: 20,
        t: 20,
        b: 50,
        pad: 0
      }
    };
  }

  /**
   * Update the title of the param graph
   * @param {any} graph - The selected graph
   * @param {number} index - The graph index in this.graphs
   */
  private updateGraphTitle(graph: any, filtersDisplayed?: any): void {
    this.translate.get("analytics").subscribe((t) => {
      const graphName: string = this.config.name;
      switch (graphName) {
        case "storage":
        case "transit":
        case "stockLevel":
          const containerLabel =
            this.containerTypes?.length > 1
              ? t.graphTitle.containers
              : `${t.graphTitle.container} ${this.containerTypes[0]}`;
          graph.customTitle = `${t.navigator[graphName].label} ${containerLabel}`;
          break;
        case "redOrangePlant":
        case "redOrangeSupplier":
        case "redPlant":
        case "redSupplier":
          let alertLabel = t.titleAddition.alertType.all;
          if (filtersDisplayed?.filterAlertType === "alert")
            alertLabel = t.titleAddition.alertType.alert;
          else if (filtersDisplayed?.filterAlertType === "warning")
            alertLabel = t.titleAddition.alertType.warning;
          let containerCategory = t.titleAddition.containerCategory.all;
          if (filtersDisplayed?.filterContainerCategory === "polyvalent")
            containerCategory = t.titleAddition.containerCategory.polyvalent;
          else if (filtersDisplayed?.filterContainerCategory === "specific")
            containerCategory = t.titleAddition.containerCategory.specific;
          graph.customTitle = `KPI: ${alertLabel} ${t.navigator[graphName].label} ${containerCategory}`;
          break;
        default:
          graph.customTitle = `${t.navigator[graphName].label} ${containerLabel}`;
      }
    });
  }

  /**
   * Update the size of the param graph
   * @param {any} graph - The selected graph
   */
  private resizeConfigLayout(graph: any): void {
    graph.layout.width = 0.97 * window.innerWidth;
    const hasDrilldown = !!this.drilldownGraph;
    graph.layout.height =
      this.config.requests?.length >= 2
        ? (hasDrilldown ? 0.3 : 0.4) * window.innerHeight
        : (hasDrilldown ? 0.45 : 0.8) * window.innerHeight;
  }

  /**
   * Apply the param function on all graph in this.graphs
   * @param {callbackFunc} cb - The callback function called for all graphs
   */
  private applyOnAllGraphs(cb: (graph: any, index: number) => void): void {
    for (let i = 0; i < this.graphs.length; i++) {
      cb(this.graphs[i], i);
    }
  }

  /**
   * Format all datas for plotly graphs
   */
  private prepareGraphs(): void {
    this.resizeConfigLayout(this.config);

    const hasOnlyPolyvalent =
      this.filterContainerCategory === "polyvalent"
        ? true
        : this.filterContainerCategory === "specific"
          ? false
          : undefined;
    const hasOnlyRed =
      this.filterAlertType === "alert"
        ? true
        : this.filterAlertType === "warning"
          ? false
          : undefined;

    this.graphs = this.datas.map((data, index) => {
      let graph = JSON.parse(JSON.stringify(this.config)); // deep copy of this.config

      graph.data = [];
      graph = this.analyticsService.formatGraphData(
        graph.requests[index],
        data,
        graph,
        {
          days: this.filterDays,
          sites: this.filterSites,
          arrivalSites: this.filterArrivalSites,
          departureSites: this.filterDepartureSites,
          hasOnlyRed,
          hasOnlyPolyvalent,
          packaging: this.filterPackaging
        }
      );

      const graphType = graph.data[0].type;
      graph.layout = {
        ...this.defaultLayout(graphType),
        ...graph.layout
      };
      graph.config = {
        ...this.defaultConfig(graphType),
        ...graph.config
      };

      return graph;
    });

    this.applyOnAllGraphs((graph) => {
      this.updateGraphTitle(graph, {
        filterAlertType: this.filterAlertType,
        filterContainerCategory: this.filterContainerCategory
      });
    });
    this.resetDrilldown();
  }

  public onBarClick(event: any, graph: any, graphIndex: number): void {
    if (!this.isCacheGraph()) return;
    const clickedSite = event?.points?.[0]?.x;
    if (!clickedSite) return;

    if (
      this.selectedSiteDrilldown?.site === clickedSite &&
      this.selectedSiteDrilldown?.graphIndex === graphIndex
    ) {
      this.resetDrilldown();
      this.applyOnAllGraphs(this.resizeConfigLayout.bind(this));
      return;
    }

    const sourceData = this.datas?.[graphIndex] as unknown as
      | (BarGraphDataDTO & {
          isRed?: boolean[];
          y_details?: Array<Array<{ pack: string; num: number }>>;
          x_labels?: string[];
        })
      | undefined;

    if (!sourceData?.x?.length || !sourceData?.y_details?.length) return;

    const drilldownData = this.buildPackagingDrilldownData(
      sourceData,
      clickedSite
    );

    if (!drilldownData.data.length) {
      this.resetDrilldown();
      return;
    }

    // Get the full site label from x_labels
    const siteIndex = sourceData.x.indexOf(clickedSite);
    const fullSiteLabel = sourceData.x_labels?.[siteIndex] || clickedSite;

    this.selectedSiteDrilldown = {
      site: clickedSite,
      graphIndex,
      graphTitle: graph.customTitle
    };

    this.drilldownGraph = {
      data: drilldownData.data,
      layout: {
        ...defaultBarLayout,
        ...graph.layout,
        barmode: "stack",
        showlegend: false,
        margin: {
          l: 80,
          r: 20,
          t: 20,
          b: 50,
          pad: 0
        },
        xaxis: {
          ...(graph.layout?.yaxis || {}),
          title:
            this.translate.instant("analytics.drilldown.countAxisTitle") ||
            "number of packaging (n)",
          rangemode: "tozero",
          autorange: true
        },
        yaxis: {
          title: "",
          type: "category",
          autorange: true
        },
        width: 0.97 * window.innerWidth,
        height: 0.25 * window.innerHeight
      },
      config: {
        ...defaultBarConfig,
        ...graph.config
      },
      customTitle:
        this.translate.instant("analytics.drilldown.title", {
          siteAbbreviation: clickedSite,
          siteLabel: fullSiteLabel,
          totalPackaging: drilldownData.totalCount
        }) || `Packaging details - ${clickedSite}`
    };

    // Resize main graphs to fit with drilldown
    this.applyOnAllGraphs(this.resizeConfigLayout.bind(this));
  }

  public closeDrilldown(): void {
    this.resetDrilldown();
    // Restore main graphs to full size
    this.applyOnAllGraphs(this.resizeConfigLayout.bind(this));
  }

  private resetDrilldown(): void {
    this.selectedSiteDrilldown = undefined;
    this.drilldownGraph = undefined;
  }

  public isCacheGraph(): boolean {
    return this.config?.requests?.every(
      (request: string) => request === "cacheGraphs"
    );
  }

  private buildPackagingDrilldownData(
    data: BarGraphDataDTO & {
      isRed?: boolean[];
      y_details?: Array<Array<{ pack: string; num: number }>>;
    },
    site: string
  ): { data: any[]; totalCount: number } {
    const packagingMap = new Map<string, number>();

    for (let i = 0; i < data.x.length; i++) {
      if (data.x[i] !== site) {
        continue;
      }

      const details = data.y_details?.[i] || [];
      if (!details.length) {
        continue;
      }

      const isRed = data.isRed ? data.isRed[i] : false;
      if (this.filterAlertType === "alert" && !isRed) {
        continue;
      }
      if (this.filterAlertType === "warning" && isRed) {
        continue;
      }

      for (const detail of details) {
        const packaging = detail.pack;
        if (!packaging) {
          continue;
        }

        if (
          this.filterPackaging?.length &&
          !this.filterPackaging.includes(packaging)
        ) {
          continue;
        }

        packagingMap.set(
          packaging,
          (packagingMap.get(packaging) || 0) + detail.num
        );
      }
    }

    const packagingAxisTitle =
      this.translate.instant("analytics.drilldown.packagingAxisTitle") ||
      "Packaging";
    const countAxisTitle =
      this.translate.instant("analytics.drilldown.countAxisTitle") ||
      "number of packaging (n)";

    const traces: any[] = Array.from(packagingMap.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([packaging, quantity], index) => ({
        x: [quantity],
        y: [site],
        name: packaging,
        type: "bar",
        orientation: "h",
        marker: { color: this.getDrilldownColor(index) },
        text: [packaging],
        textposition: "inside",
        insidetextanchor: "middle",
        hovertemplate:
          `<b>${packagingAxisTitle}:</b> ${packaging}<br>` +
          `<b>${countAxisTitle}:</b> ${quantity}<extra></extra>`
      }));

    const totalCount = traces.reduce((sum, trace) => sum + trace.x[0], 0);

    return {
      data: traces,
      totalCount
    };
  }

  private getDrilldownColor(index: number): string {
    return COLOR_PALETTE[index % COLOR_PALETTE.length];
  }

  private defaultLayout(graphType: GraphType): Object {
    switch (graphType) {
      case GraphType.BAR:
        return defaultBarLayout;
      case GraphType.LINE:
        return defaultLineLayout;
    }
  }

  private defaultConfig(graphType: GraphType): Object {
    switch (graphType) {
      case GraphType.BAR:
        return defaultBarConfig;
      case GraphType.LINE:
        return defaultLineConfig;
    }
  }
}
